import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Login.css';

type AuthMode = 'signin' | 'signup' | 'confirm' | 'newpassword' | 'mfa' | 'mfasetup' | 'mfaselect' | 'forgotpassword' | 'resetpassword';

const Login: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState<AuthMode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const { signIn, signUp, confirmSignUp, completeNewPassword, confirmMfaCode, confirmTotpSetup, selectMfaType, forgotPassword, confirmForgotPassword, isAuthenticated, needsNewPassword, needsMfaCode, needsMfaSetup, needsMfaSelection, availableMfaTypes, mfaType, totpSetupUri } = useAuth();
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [setupCode, setSetupCode] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (searchParams.get('mode') === 'signup') {
      setMode('signup');
    }
  }, [searchParams]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (needsNewPassword) {
      setMode('newpassword');
    }
  }, [needsNewPassword]);

  useEffect(() => {
    if (needsMfaCode) {
      setMode('mfa');
    }
  }, [needsMfaCode]);

  useEffect(() => {
    if (needsMfaSetup) {
      setMode('mfasetup');
    }
  }, [needsMfaSetup]);

  useEffect(() => {
    if (needsMfaSelection) {
      // Auto-select TOTP (Authenticator App) — skip the method selection screen
      selectMfaType('TOTP').catch(() => setMode('mfaselect'));
    }
  }, [needsMfaSelection]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signIn(email, password);
      // Navigation happens via useEffect when isAuthenticated or needsNewPassword changes
    } catch (err: any) {
      setError(err.message || 'Failed to sign in');
    } finally {
      setLoading(false);
    }
  };

  const handleNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmNewPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      await completeNewPassword(newPassword);
      // Navigation happens via useEffect when isAuthenticated changes
    } catch (err: any) {
      setError(err.message || 'Failed to set new password');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await confirmMfaCode(mfaCode);
      // Navigation happens via useEffect when isAuthenticated changes
    } catch (err: any) {
      setError(err.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleTotpSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await confirmTotpSetup(setupCode);
      // Navigation happens via useEffect when isAuthenticated changes
    } catch (err: any) {
      setError(err.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSelection = async (type: string) => {
    setError('');
    setLoading(true);

    try {
      await selectMfaType(type);
    } catch (err: any) {
      setError(err.message || 'Failed to select verification method');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await forgotPassword(email);
      setMessage('Reset code sent to your email.');
      setMode('resetpassword');
    } catch (err: any) {
      setError(err.message || 'Failed to send reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmNewPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      await confirmForgotPassword(email, resetCode, newPassword);
      setMessage('Password reset successful! You can now sign in.');
      setMode('signin');
      setNewPassword('');
      setConfirmNewPassword('');
      setResetCode('');
    } catch (err: any) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      await signUp(email, password);
      setMessage('Account created! Check your email for the confirmation code.');
      setMode('confirm');
    } catch (err: any) {
      setError(err.message || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await confirmSignUp(email, confirmationCode);
      setMessage('Account confirmed! You can now sign in.');
      setMode('signin');
    } catch (err: any) {
      setError(err.message || 'Failed to confirm account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-logo">
        <img src="/logo.png" alt="SilverScribe" />
      </div>
      <div className="login-container">
        <h1 className="login-title">
          {mode === 'signin' && 'Sign In'}
          {mode === 'signup' && 'Create Account'}
          {mode === 'confirm' && 'Confirm Account'}
          {mode === 'newpassword' && 'Set New Password'}
          {mode === 'mfa' && 'Enter Verification Code'}
          {mode === 'mfasetup' && 'Set Up Authenticator'}
          {mode === 'mfaselect' && 'Choose Verification Method'}
          {mode === 'forgotpassword' && 'Forgot Password'}
          {mode === 'resetpassword' && 'Reset Password'}
        </h1>

        {error && <div className="alert alert-error">{error}</div>}
        {message && <div className="alert alert-success">{message}</div>}

        {mode === 'signin' && (
          <form onSubmit={handleSignIn} className="login-form">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Enter your email"
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter your password"
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
            <button
              type="button"
              className="link-button forgot-password-link"
              onClick={() => setMode('forgotpassword')}
            >
              Forgot password?
            </button>
          </form>
        )}

        {mode === 'signup' && (
          <form onSubmit={handleSignUp} className="login-form">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Enter your email"
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="At least 8 characters"
              />
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="Confirm your password"
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
          </form>
        )}

        {mode === 'confirm' && (
          <form onSubmit={handleConfirm} className="login-form">
            <p className="confirm-text">
              Enter the confirmation code sent to your email.
              <br />
              <small>(For testing, use code: 123456)</small>
            </p>
            <div className="form-group">
              <label htmlFor="code">Confirmation Code</label>
              <input
                type="text"
                id="code"
                value={confirmationCode}
                onChange={(e) => setConfirmationCode(e.target.value)}
                required
                placeholder="Enter 6-digit code"
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Confirming...' : 'Confirm Account'}
            </button>
          </form>
        )}

        {mode === 'newpassword' && (
          <form onSubmit={handleNewPassword} className="login-form">
            <p className="confirm-text">
              Your account requires a new password.
              <br />
              Please set a new password to continue.
            </p>
            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                placeholder="At least 8 characters"
              />
            </div>
            <div className="form-group">
              <label htmlFor="confirmNewPassword">Confirm New Password</label>
              <input
                type="password"
                id="confirmNewPassword"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                required
                placeholder="Confirm your new password"
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Setting password...' : 'Set New Password'}
            </button>
          </form>
        )}

        {mode === 'mfa' && (
          <form onSubmit={handleMfaCode} className="login-form">
            <p className="confirm-text">
              {mfaType === 'SMS'
                ? 'Enter the verification code sent to your phone.'
                : mfaType === 'EMAIL'
                ? 'Enter the verification code sent to your email.'
                : 'Enter the code from your authenticator app.'}
            </p>
            <div className="form-group">
              <label htmlFor="mfaCode">Verification Code</label>
              <input
                type="text"
                id="mfaCode"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                required
                placeholder="Enter 6-digit code"
                autoComplete="one-time-code"
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Verifying...' : 'Verify'}
            </button>
          </form>
        )}

        {mode === 'mfasetup' && (
          <form onSubmit={handleTotpSetup} className="login-form">
            <p className="confirm-text">
              Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.), then enter the code below.
            </p>
            {totpSetupUri && (
              <div className="qr-code-container" style={{ textAlign: 'center', margin: '20px 0' }}>
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(totpSetupUri)}`}
                  alt="QR Code for authenticator setup"
                  style={{ border: '1px solid #ccc', borderRadius: '8px' }}
                />
              </div>
            )}
            <div className="form-group">
              <label htmlFor="setupCode">Verification Code</label>
              <input
                type="text"
                id="setupCode"
                value={setupCode}
                onChange={(e) => setSetupCode(e.target.value)}
                required
                placeholder="Enter 6-digit code from app"
                autoComplete="one-time-code"
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Verifying...' : 'Complete Setup'}
            </button>
          </form>
        )}

        {mode === 'mfaselect' && (
          <div className="login-form">
            <p className="confirm-text">
              Choose how you'd like to verify your identity.
            </p>
            {availableMfaTypes.map((type) => (
              <button
                key={type}
                className="btn btn-primary"
                style={{ width: '100%', marginBottom: '0.75rem' }}
                onClick={() => handleMfaSelection(type)}
                disabled={loading}
              >
                {type === 'TOTP' ? 'Authenticator App' : type === 'EMAIL' ? 'Email Code' : type === 'SMS' ? 'Text Message' : type}
              </button>
            ))}
          </div>
        )}

        {mode === 'forgotpassword' && (
          <form onSubmit={handleForgotPassword} className="login-form">
            <p className="confirm-text">
              Enter your email address and we'll send you a code to reset your password.
            </p>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Enter your email"
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Sending...' : 'Send Reset Code'}
            </button>
            <button
              type="button"
              className="link-button"
              onClick={() => setMode('signin')}
            >
              Back to Sign In
            </button>
          </form>
        )}

        {mode === 'resetpassword' && (
          <form onSubmit={handleResetPassword} className="login-form">
            <p className="confirm-text">
              Enter the code sent to your email and your new password.
            </p>
            <div className="form-group">
              <label htmlFor="resetCode">Reset Code</label>
              <input
                type="text"
                id="resetCode"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value)}
                required
                placeholder="Enter 6-digit code"
              />
            </div>
            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                placeholder="At least 8 characters"
              />
            </div>
            <div className="form-group">
              <label htmlFor="confirmNewPassword">Confirm New Password</label>
              <input
                type="password"
                id="confirmNewPassword"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                required
                placeholder="Confirm your new password"
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
            <button
              type="button"
              className="link-button"
              onClick={() => setMode('signin')}
            >
              Back to Sign In
            </button>
          </form>
        )}

        <div className="login-footer">
          {mode === 'signin' && (
            <p>
              Need an account?{' '}
              <a href="/contact" className="link-button">
                Contact Us
              </a>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
